import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  FormControl,
  Grid,
  Stack,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  FormHelperText,
  DialogTitle,
  IconButton,
} from '@mui/material';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import dayjs from 'dayjs';
import { X } from 'lucide-react';
import { dukaElements } from '../../../api/Duka/api';

const PROJECTION_STATUS = {
  DRAFT: 'DRAFT',
  ORDERED: 'ORDERED',
};

const ProjectionForm = ({ initialData, onSubmit, onCancel }) => {
  const [dukas, setDukas] = useState([]);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});

  const [formData, setFormData] = useState({
    duka: initialData?.duka || '',
    product: initialData?.product || '',
    forecast_date: initialData?.forecast_date ? dayjs(initialData.forecast_date) : null,
    forecasted_quantity: initialData?.forecasted_quantity || '',
    final_quantity: initialData?.final_quantity || '',
    status: initialData?.status || PROJECTION_STATUS.DRAFT,
    proposal_reason: initialData?.proposal_reason || '',
  });

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        const [dukaResponse, productResponse] = await Promise.all([
          dukaElements('dukas'),
          dukaElements('products'),
        ]);
        setDukas(dukaResponse.data);
        setProducts(productResponse.data);
      } catch (error) {
        console.error('Error fetching data:', error);
        setErrors({ general: 'Failed to load dukas and products' });
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  const validateForm = () => {
    const newErrors = {};

    if (!formData.duka) {
      newErrors.duka = 'Duka is required';
    }
    if (!formData.product) {
      newErrors.product = 'Product is required';
    }
    if (!formData.forecast_date) {
      newErrors.forecast_date = 'Forecast date is required';
    }
    if (!formData.forecasted_quantity || formData.forecasted_quantity < 1) {
      newErrors.forecasted_quantity = 'Forecasted quantity must be at least 1';
    }
    if (formData.final_quantity && formData.final_quantity < 0) {
      newErrors.final_quantity = 'Final quantity cannot be negative';
    }
    if (!formData.status) {
      newErrors.status = 'Status is required';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
    if (errors[name]) {
      setErrors((prev) => ({
        ...prev,
        [name]: undefined,
      }));
    }
  };

  const handleDateChange = (newValue) => {
    setFormData((prev) => ({
      ...prev,
      forecast_date: newValue,
    }));
    if (errors.forecast_date) {
      setErrors((prev) => ({
        ...prev,
        forecast_date: undefined,
      }));
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    const submissionData = {
      duka: formData.duka,
      product: formData.product,
      forecast_date: formData.forecast_date ? formData.forecast_date.format('YYYY-MM-DD') : null,
      forecasted_quantity: parseInt(formData.forecasted_quantity) || 0,
      final_quantity: formData.final_quantity ? parseInt(formData.final_quantity) : null,
      status: formData.status,
      proposal_reason: formData.proposal_reason || 'Manually created',
    };

    onSubmit(submissionData);
  };

  return (
    <Box sx={{ p: 2 }}>
      <DialogTitle
        sx={{
          p: 1,
          fontSize: 18,
          fontWeight: 500,
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
        }}
      >
        {initialData ? 'Edit Projection' : 'Add Projection'}
        <IconButton
          onClick={onCancel}
          size="small"
          sx={{ color: 'text.secondary' }}
        >
          <X size={20} />
        </IconButton>
      </DialogTitle>
      <form onSubmit={handleSubmit}>
        <Grid container spacing={3}>
          {errors.general && (
            <Grid item xs={12}>
              <FormHelperText error>{errors.general}</FormHelperText>
            </Grid>
          )}
          <Grid item xs={12}>
            <FormControl fullWidth error={!!errors.duka}>
              <InputLabel id="duka-label">Duka</InputLabel>
              <Select
                labelId="duka-label"
                name="duka"
                value={formData.duka}
                onChange={handleChange}
                label="Duka"
                required
                disabled={loading}
              >
                {dukas.map((duka) => (
                  <MenuItem key={duka.id} value={duka.id}>
                    {duka.code} | {duka.name}
                  </MenuItem>
                ))}
              </Select>
              {errors.duka && <FormHelperText>{errors.duka}</FormHelperText>}
            </FormControl>
          </Grid>

          <Grid item xs={12}>
            <FormControl fullWidth error={!!errors.product}>
              <InputLabel id="product-label">Product</InputLabel>
              <Select
                labelId="product-label"
                name="product"
                value={formData.product}
                onChange={handleChange}
                label="Product"
                required
                disabled={loading}
              >
                {products.map((product) => (
                  <MenuItem key={product.id} value={product.id}>
                    {product.code} | {product.name}
                  </MenuItem>
                ))}
              </Select>
              {errors.product && <FormHelperText>{errors.product}</FormHelperText>}
            </FormControl>
          </Grid>

          <Grid item xs={12}>
            <LocalizationProvider dateAdapter={AdapterDayjs}>
              <DatePicker
                label="Forecast Date"
                value={formData.forecast_date}
                onChange={handleDateChange}
                slotProps={{
                  textField: {
                    fullWidth: true,
                    required: true,
                    error: !!errors.forecast_date,
                    helperText: errors.forecast_date,
                  },
                }}
              />
            </LocalizationProvider>
          </Grid>

          <Grid item xs={12}>
            <TextField
              fullWidth
              label="Forecasted Quantity"
              name="forecasted_quantity"
              type="number"
              value={formData.forecasted_quantity}
              onChange={handleChange}
              required
              error={!!errors.forecasted_quantity}
              helperText={errors.forecasted_quantity}
              InputProps={{ inputProps: { min: 1 } }}
            />
          </Grid>

          <Grid item xs={12}>
            <TextField
              fullWidth
              label="Final Quantity"
              name="final_quantity"
              type="number"
              value={formData.final_quantity}
              onChange={handleChange}
              error={!!errors.final_quantity}
              helperText={errors.final_quantity}
              InputProps={{ inputProps: { min: 0 } }}
            />
          </Grid>

          <Grid item xs={12}>
            <FormControl fullWidth error={!!errors.status}>
              <InputLabel id="status-label">Status</InputLabel>
              <Select
                labelId="status-label"
                name="status"
                value={formData.status}
                onChange={handleChange}
                label="Status"
                required
                disabled={loading}
              >
                {Object.entries(PROJECTION_STATUS).map(([key, value]) => (
                  <MenuItem key={key} value={value}>
                    {value}
                  </MenuItem>
                ))}
              </Select>
              {errors.status && <FormHelperText>{errors.status}</FormHelperText>}
            </FormControl>
          </Grid>

          <Grid item xs={12}>
            <TextField
              fullWidth
              label="Proposal Reason"
              name="proposal_reason"
              value={formData.proposal_reason}
              onChange={handleChange}
              multiline
              rows={4}
              InputLabelProps={{ shrink: true }}
              sx={{
                '& .MuiInputBase-root': {
                  alignItems: 'flex-start',
                  paddingTop: '8px',
                },
              }}
            />
          </Grid>

          <Grid item xs={12}>
            <Stack direction="row" spacing={2} justifyContent="flex-end">
              <Button
                variant="contained"
                onClick={onCancel}
                sx={{
                  bgcolor: '#e0e0e0',
                  color: '#000',
                  '&:hover': { bgcolor: '#d5d5d5' },
                  textTransform: 'none',
                  borderRadius: 2,
                  fontWeight: 600,
                  px: 3,
                  fontSize: '16px',
                  lineHeight: '24px',
                }}
              >
                Cancel
              </Button>
              <Button
                variant="contained"
                type="submit"
                disabled={loading}
                sx={{
                  bgcolor: '#00694B',
                  color: '#fff',
                  '&:hover': { bgcolor: '#005039' },
                  textTransform: 'none',
                  borderRadius: 2,
                  fontWeight: 600,
                  px: 3,
                  fontSize: '16px',
                  lineHeight: '24px',
                }}
              >
                {initialData ? 'Update Projection' : 'Add Projection'}
              </Button>
            </Stack>
          </Grid>
        </Grid>
      </form>
    </Box>
  );
};

export default ProjectionForm;